import { useMemo } from 'react';
import { startOfDay, addDays, isAfter } from 'date-fns';
import type { CollaboratorAllocation } from '@/features/Collaborators/Models';
import { isAbsence, isActiveOnDate, isIgnoredForOccupation } from '../utils/allocationDates';

export type AllocationPreviewStatus =
  | 'Absence'
  | 'Idle'
  | 'IdleUpcoming'
  | 'NeedsReallocation'
  | 'Allocated';

export interface AllocationPreviewResult {
  status: AllocationPreviewStatus;
  label: string;
  description: string;
}

const STATUS_METADATA: Record<
  AllocationPreviewStatus,
  { label: string; description: string }
> = {
  Absence: {
    label: 'Ausente',
    description: 'Colaboradores ausentes',
  },
  Idle: {
    label: 'Ocioso',
    description: 'Colaboradores ociosos',
  },
  IdleUpcoming: {
    label: 'Alocação prevista',
    description: 'Ociosos com alocação iniciando em breve',
  },
  NeedsReallocation: {
    label: 'Precisa de realocação',
    description: 'Colaboradores que ficarão ociosos em até 30 dias',
  },
  Allocated: {
    label: 'Alocado',
    description: 'Colaboradores alocados',
  },
};

const getDate = (value?: any | null): Date | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  if (typeof value.toDate === 'function') {
    return value.toDate();
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const hasAbsenceToday = (
  allocations: CollaboratorAllocation[],
  today: Date
): boolean =>
  allocations.some(
    (allocation) =>
      isAbsence(allocation) && isActiveOnDate(allocation, today)
  );

const getRelevantAllocations = (
  allocations: CollaboratorAllocation[]
): CollaboratorAllocation[] =>
  allocations.filter((allocation) => !isIgnoredForOccupation(allocation));

const hasActiveOrUpcomingAllocation = (
  allocations: CollaboratorAllocation[],
  today: Date,
  withinDays: number
): boolean => {
  const limitDate = addDays(startOfDay(today), withinDays);

  return allocations.some((allocation) => {
    const startDate = getDate(allocation.startDate);
    if (!startDate) {
      return false;
    }

    const startDay = startOfDay(startDate);

    if (isActiveOnDate(allocation, today)) {
      return true;
    }

    return (
      isAfter(startDay, startOfDay(today)) &&
      !isAfter(startDay, limitDate)
    );
  });
};

const willBecomeIdleWithin = (
  allocations: CollaboratorAllocation[],
  today: Date,
  withinDays: number
): boolean => {
  const limitDate = addDays(startOfDay(today), withinDays);

  const relevantAllocations = getRelevantAllocations(allocations).filter(
    (allocation) => {
      const endDate = getDate(allocation.endDate);
      if (!endDate) {
        return true;
      }

      return !isAfter(startOfDay(endDate), limitDate);
    }
  );

  if (relevantAllocations.length === 0) {
    return true;
  }

  const sortedAllocations = relevantAllocations.sort((a, b) => {
    const startA = getDate(a.startDate)?.getTime() ?? 0;
    const startB = getDate(b.startDate)?.getTime() ?? 0;
    return startA - startB;
  });

  let coverageEnd: Date | null = null;

  for (const allocation of sortedAllocations) {
    const start = getDate(allocation.startDate);
    const end = getDate(allocation.endDate);

    if (!start) {
      continue;
    }

    const normalizedStart = startOfDay(start);
    const normalizedEnd = end ? startOfDay(end) : null;

    if (
      !coverageEnd ||
      coverageEnd.getTime() < normalizedStart.getTime()
    ) {
      coverageEnd = normalizedEnd ?? null;
    } else if (normalizedEnd) {
      coverageEnd =
        coverageEnd && coverageEnd.getTime() > normalizedEnd.getTime()
          ? coverageEnd
          : normalizedEnd;
    }

    if (!normalizedEnd) {
      return false;
    }

    if (coverageEnd && isAfter(coverageEnd, limitDate)) {
      return false;
    }
  }

  return true;
};

export function useCollaboratorAllocationPreviewStatus(
  allocations: CollaboratorAllocation[],
  referenceDate: Date = new Date()
): AllocationPreviewResult {
  return useMemo(() => {
    const today = startOfDay(referenceDate);

    if (hasAbsenceToday(allocations, today)) {
      return { status: 'Absence', ...STATUS_METADATA.Absence };
    }

    const relevantAllocations = getRelevantAllocations(allocations);

    const hasCurrentOrUpcomingAllocation = hasActiveOrUpcomingAllocation(
      relevantAllocations,
      today,
      7
    );

    if (!hasCurrentOrUpcomingAllocation) {
      return { status: 'Idle', ...STATUS_METADATA.Idle };
    }

    const hasFutureWithin7Days = relevantAllocations.some((allocation) => {
      const startDate = getDate(allocation.startDate);
      if (!startDate) {
        return false;
      }

      const normalizedStart = startOfDay(startDate);
      return (
        isAfter(normalizedStart, today) &&
        !isAfter(normalizedStart, addDays(today, 7))
      );
    });

    if (!isActiveOnDateList(relevantAllocations, today) && hasFutureWithin7Days) {
      return { status: 'IdleUpcoming', ...STATUS_METADATA.IdleUpcoming };
    }

    if (willBecomeIdleWithin(relevantAllocations, today, 30)) {
      return {
        status: 'NeedsReallocation',
        ...STATUS_METADATA.NeedsReallocation,
      };
    }

    return { status: 'Allocated', ...STATUS_METADATA.Allocated };
  }, [allocations, referenceDate]);
}

const isActiveOnDateList = (
  allocations: CollaboratorAllocation[],
  date: Date
): boolean => allocations.some((allocation) => isActiveOnDate(allocation, date));

export default useCollaboratorAllocationPreviewStatus;
